import styles from './ModelLayout.module.css'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useCallback, useEffect, useState, useRef} from 'react'
import {
  Breadcrumbs,
  CounterLabel,
  Label,
  Text,
  UnderlineNav,
  Link,
  Box,
  PageLayout,
  Button,
  FormControl,
  IconButton,
  Textarea,
  useResponsiveValue,
} from '@primer/react'

import {
  CodeIcon,
  BookIcon,
  LawIcon,
  EllipsisIcon,
  PaperAirplaneIcon,
  ArrowUpRightIcon,
  ChecklistIcon,
  CommandPaletteIcon,
  LogIcon,
} from '@primer/octicons-react'
import type {Model, ShowModelPayload} from '../../types'
import {templateRepositoryNwo} from './playground-types'
import {normalizeModelPublisher} from '../../utilities/normalize-model-strings'

import ModelSwitcher from './ModelSwitcher'
import {ModelLegalTerms} from './ModelLegalTerms'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import GettingStartedDialog from './GettingStartedDialog/GettingStartedDialog'

export const modelUrl = (model: Model) => `/marketplace/models/${model.registry}/${model.name}`

export function ModelLayout({
  activeTab,
  children,
}: React.PropsWithChildren<{
  activeTab: 'playground' | 'readme' | 'getting-started' | 'example-prompts' | 'transparency' | 'evaluation' | 'license'
}>) {
  const {model, modelLicense, on_waitlist} = useRoutePayload<ShowModelPayload>()
  const thisModelUrl = modelUrl(model)
  const noEvaluationReport = 'This model does not provide an evaluation report.'
  const noTransparencyNotes = 'The model does not have specific notes.'
  const noAdditionalTransparencyNotes = 'This model does not provide any additional notes.'

  if (activeTab === 'playground') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100dvh - 64px)',
        }}
      >
        <Box
          sx={{
            display: ['block', 'block', 'none'],
          }}
        >
          <GiveFeedback mobile />
        </Box>
        <Box sx={{height: '100%', width: '100%', p: 3}}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              width: '100%',
              height: '100%',
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                maxHeight: '100%',
                height: '100%',
              }}
            >
              <PlaygroundHeader model={model} />
              {children}
            </Box>
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <div className="d-flex flex-column">
      <Box
        sx={{
          display: ['block', 'block', 'none'],
        }}
      >
        <GiveFeedback mobile />
      </Box>

      <PageLayout columnGap="normal">
        <PageLayout.Header>
          <OverviewHeader model={model} />
        </PageLayout.Header>

        <PageLayout.Content as="div">
          {model.task === 'chat-completion' && (
            <Box sx={{py: 2}}>
              <MiniPlayground model={model} onWaitlist={on_waitlist} />
            </Box>
          )}
          <Box
            sx={{
              width: '100%',
              borderColor: 'border.default',
              borderStyle: 'solid',
              borderWidth: 1,
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: 'var(--shadow-resting-small,var(--color-shadow-small))',
            }}
          >
            <Box>
              <UnderlineNav aria-label="Model navigation">
                <UnderlineNav.Item
                  icon={BookIcon}
                  href={`${thisModelUrl}`}
                  aria-current={activeTab === 'readme' ? 'page' : undefined}
                >
                  README
                </UnderlineNav.Item>
                {model.evaluation && noEvaluationReport !== model.evaluation && (
                  <UnderlineNav.Item
                    icon={ChecklistIcon}
                    href={`${thisModelUrl}/evaluation`}
                    aria-current={activeTab === 'evaluation' ? 'page' : undefined}
                  >
                    Evaluation
                  </UnderlineNav.Item>
                )}
                {model.notes &&
                  noTransparencyNotes !== model.notes &&
                  noAdditionalTransparencyNotes !== model.notes && (
                    <UnderlineNav.Item
                      icon={LogIcon}
                      href={`${thisModelUrl}/transparency`}
                      aria-current={activeTab === 'transparency' ? 'page' : undefined}
                    >
                      Transparency
                    </UnderlineNav.Item>
                  )}
                {modelLicense && (
                  <UnderlineNav.Item
                    icon={LawIcon}
                    href={`${thisModelUrl}/license`}
                    aria-current={activeTab === 'license' ? 'page' : undefined}
                  >
                    License
                  </UnderlineNav.Item>
                )}
              </UnderlineNav>
            </Box>
            <Box sx={{p: [2, 2, 3]}}>{children}</Box>
          </Box>
          <Box sx={{display: ['block', 'block', 'none'], pb: 5, pt: 3}}>
            <SidebarInfo model={model} />
          </Box>
        </PageLayout.Content>
        <PageLayout.Pane hidden={{narrow: true, regular: false}}>
          <SidebarInfo model={model} />
        </PageLayout.Pane>
      </PageLayout>
    </div>
  )
}

function BreadcrumbHeader({model}: {model: Model}) {
  return (
    <Breadcrumbs>
      <Breadcrumbs.Item href="/marketplace">Marketplace</Breadcrumbs.Item>
      <Breadcrumbs.Item href="/marketplace/models">Models</Breadcrumbs.Item>
      <Breadcrumbs.Item href="" selected>
        {normalizeModelPublisher(model.publisher)}
      </Breadcrumbs.Item>
    </Breadcrumbs>
  )
}

function OverviewHeader({model}: {model: Model}) {
  const {summary, tags} = model
  const openInCodespacesUrl = `/codespaces/new?template_repository=${templateRepositoryNwo}`
  const [isGettingStartedDialogOpen, setGettingStartedDialogOpen] = useState(false)
  const playgroundEnabled = useFeatureFlag('project_neutron_playground')

  return (
    <Box sx={{display: 'flex', gap: 3, flexDirection: 'column'}}>
      <BreadcrumbHeader model={model} />
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: ['column', 'column', 'row'],
            alignItems: 'center',
            gap: 3,
          }}
        >
          <Box
            sx={{
              flexGrow: [1, 1, 1],
              display: 'flex',
              minWidth: 0,
              alignItems: 'center',
              width: ['100%', '100%', 'auto'],
            }}
          >
            <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2}}>
              <GitHubAvatar square size={32} src={model.logo_url} />
              <Text as="h1" className="h3">
                {model.friendly_name}
              </Text>
            </Box>
          </Box>

          <Box
            sx={{
              display: ['flex', 'flex', 'none'],
              flexDirection: 'column',
              width: '100%',
              gap: 3,
            }}
          >
            <Text sx={{lineHeight: 1.5}}>{summary}</Text>
            {tags.length > 0 && (
              <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                {tags.map(tag => (
                  <a href={`/topics/${tag}`} key={tag} className="topic-tag topic-tag-link">
                    {tag}
                  </a>
                ))}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              width: ['100%', '100%', 'auto'],
              justifyContent: 'flex-end',
              flexGrow: [0, 0, 1],
              flexShrink: 0,
              flexDirection: ['column-reverse', 'column-reverse', 'row'],
            }}
          >
            <Box sx={{display: ['none', 'none', 'flex']}}>
              <GiveFeedback />
            </Box>
            {model.task === 'chat-completion' && (
              <Button
                variant="default"
                as="a"
                href={`${modelUrl(model)}/playground`}
                leadingVisual={CommandPaletteIcon}
              >
                Playground
              </Button>
            )}
            <Button leadingVisual={CodeIcon} variant="primary" onClick={() => setGettingStartedDialogOpen(true)}>
              Get started
            </Button>
            {isGettingStartedDialogOpen && (
              <GettingStartedDialog
                openInCodespaceUrl={openInCodespacesUrl}
                onClose={() => setGettingStartedDialogOpen(false)}
                playgroundEnabled={playgroundEnabled}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

function PlaygroundHeader({model}: {model: Model}) {
  const openInCodespacesUrl = `/codespaces/new?template_repository=${templateRepositoryNwo}`
  const [isGettingStartedDialogOpen, setGettingStartedDialogOpen] = useState(false)
  const playgroundEnabled = useFeatureFlag('project_neutron_playground')
  return (
    <Box sx={{display: 'flex', gap: 3, flexDirection: 'column', pb: 3}}>
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              minWidth: 0,
              alignItems: 'center',
              width: 'auto',
            }}
          >
            <ModelSwitcher model={model} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              width: 'auto',
              justifyContent: 'flex-end',
              flexGrow: 1,
              flexShrink: 0,
              flexDirection: 'row',
            }}
          >
            <Box sx={{display: ['none', 'none', 'flex']}}>
              <GiveFeedback />
            </Box>
            <Button leadingVisual={CodeIcon} variant="primary" onClick={() => setGettingStartedDialogOpen(true)}>
              Get started
            </Button>
            {isGettingStartedDialogOpen && (
              <GettingStartedDialog
                openInCodespaceUrl={openInCodespacesUrl}
                onClose={() => setGettingStartedDialogOpen(false)}
                playgroundEnabled={playgroundEnabled}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

function GiveFeedback({mobile}: {mobile?: boolean}) {
  if (mobile) {
    return (
      <Box
        sx={{
          display: 'flex',
          py: 3,
          px: 3,
          bg: 'canvas.subtle',
          borderBottomColor: 'border.default',
          borderBottomWidth: 1,
          borderBottomStyle: 'solid',
        }}
      >
        <Box sx={{flex: 1, fontSize: 0, color: 'fg.muted'}}>Thoughts on this? </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            gap: 'var(--base-size-6)',
            fontSize: 0,
          }}
        >
          <Label variant="success">Beta</Label>
          <Link href="https://github.com/orgs/community/discussions">Give feedback</Link>
        </Box>
      </Box>
    )
  }
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        mr: 2,
        gap: 'var(--base-size-6)',
      }}
    >
      <Label variant="success">Beta</Label>
      <Link href="https://github.com/orgs/community/discussions" sx={{fontSize: 0, whiteSpace: 'nowrap'}}>
        Give feedback
      </Link>
    </Box>
  )
}

function MiniPlayground({model, onWaitlist}: {model: Model; onWaitlist?: boolean}) {
  const [text, setText] = useState('')
  const [hasFocus, setHasFocus] = useState<boolean>(false)
  const isMobile = useResponsiveValue({narrow: true}, false)
  const playgroundEnabled = useFeatureFlag('project_neutron_playground')

  const miniPlaygroundRef = useRef<HTMLFormElement>(null)
  const miniPlaygroundInputRef = useRef<HTMLTextAreaElement>(null)

  const legalContainerRef = useRef<HTMLDivElement>(null)
  const icebreakerContainerRef = useRef<HTMLDivElement>(null)
  const animationContainerRef = useRef<HTMLDivElement>(null)

  const samplesToContent = model.samples?.inputs?.map(input => {
    const firstMessageWithContent = input.messages?.find(message => message.content)
    return firstMessageWithContent?.content
  })

  const samplesWithContent = samplesToContent?.filter(sample => sample !== undefined) as string[]
  const samplesFirstFour = samplesWithContent?.slice(0, 4)

  const handlePlaygroundCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.currentTarget.parentNode instanceof HTMLFormElement) {
      e.currentTarget.parentNode.submit()
    }
  }

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.key === 'Escape') {
      e.preventDefault()
      setText('')
    }

    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    else if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.nativeEvent.isComposing) {
      const form = miniPlaygroundRef.current
      if (form) {
        e.preventDefault()
        form.submit()
      }
    }
  }

  const adjustHeights = useCallback(() => {
    if (animationContainerRef.current && legalContainerRef.current && icebreakerContainerRef.current) {
      animationContainerRef.current.style.height = hasFocus
        ? `${legalContainerRef.current.offsetHeight}px`
        : `${icebreakerContainerRef.current.offsetHeight}px`
    }
  }, [hasFocus])

  useEffect(() => {
    adjustHeights()
  }, [hasFocus, adjustHeights])

  useEffect(() => {
    const observer = new ResizeObserver(adjustHeights)
    if (legalContainerRef.current) observer.observe(legalContainerRef.current)
    if (icebreakerContainerRef.current) observer.observe(icebreakerContainerRef.current)

    return () => {
      observer.disconnect()
    }
  }, [adjustHeights])

  const playgroundURL = `${modelUrl(model)}/playground`

  return (
    <>
      <Text
        sx={{
          fontWeight: 'bold',
          display: ['block', 'block', 'none'],
          mb: 3,
        }}
      >
        Playground
      </Text>
      {playgroundEnabled ? (
        <>
          <Box as="form" sx={{mb: 3, pt: 1, position: 'relative'}} action={playgroundURL} ref={miniPlaygroundRef}>
            <FormControl>
              <FormControl.Label visuallyHidden>Mini Playground Prompt Input</FormControl.Label>
              <Textarea
                name="p"
                id="miniplayground_icebreaker"
                ref={miniPlaygroundInputRef}
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setHasFocus(true)}
                onBlur={() => setHasFocus(false)}
                rows={isMobile ? 2 : 1}
                placeholder={`Enter a message to try ${model.friendly_name}...`}
                resize="none"
                block
                sx={{pr: 5}}
              />

              <IconButton
                icon={PaperAirplaneIcon}
                variant="invisible"
                aria-label="Open in playground"
                onClick={() => miniPlaygroundRef.current?.submit()}
                sx={{
                  position: 'absolute',
                  right: 0,
                  bottom: 0,
                  mb: 2,
                  mr: 2,
                }}
              />
            </FormControl>
          </Box>
          <Box sx={{pb: 3}}>
            <Box sx={{position: 'relative'}} className={styles.animateHeight} ref={animationContainerRef}>
              <Box
                sx={{position: 'absolute', top: 0}}
                ref={legalContainerRef}
                className={`${styles.fadeInOut} ${hasFocus ? styles.fadeInOutShow : ''}`}
              >
                <Box sx={{marginTop: '-2px'}}>
                  <ModelLegalTerms />
                </Box>
              </Box>

              {samplesFirstFour && (
                <Box
                  ref={icebreakerContainerRef}
                  className={`${styles.fadeInOut} ${hasFocus ? '' : styles.fadeInOutShow}`}
                  sx={{
                    position: 'relative', // necessary for z-index to work
                    display: 'grid',
                    gridTemplateColumns: [
                      '1fr',
                      '1fr',
                      '1fr 1fr',
                      Array(samplesFirstFour.length).fill('1fr').join(' '),
                    ],
                    gap: 3,
                  }}
                >
                  {samplesFirstFour.map(sample => (
                    <Box as="form" sx={{display: 'flex'}} key={sample} action={playgroundURL}>
                      <input type="hidden" value={sample} name="p" />
                      <PlaygroundCard sample={sample} onClick={handlePlaygroundCardClick} />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </>
      ) : (
        <Box sx={{pb: 3}}>
          <Box
            sx={{
              bg: 'done.subtle',
              borderColor: 'done.muted',
              borderStyle: 'solid',
              borderWidth: 1,
              borderRadius: 2,
              flexDirection: ['column', 'column', 'row'],
              px: 4,
              py: 3,
              display: 'flex',
              alignItems: 'flex-start',
              gap: [2, 2, 3],
            }}
          >
            <Box sx={{display: 'flex', flexDirection: 'column', flex: 1, gap: [1, 1, 0]}}>
              <Text sx={{fontWeight: 'bold', lineHeight: '20px'}}>Get early access to our playground for models</Text>
              <Text sx={{color: 'fg.muted', lineHeight: '20px'}}>
                {onWaitlist
                  ? "You're already on the waitlist! We'll send you an email once your access is granted."
                  : 'Join our limited beta waiting list today and be among the first to try out an easy way to test models'}
              </Text>
            </Box>
            <Box sx={{height: '100%', py: 1}}>
              <Button as="a" href="/marketplace/models/waitlist">
                Join the waitlist
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </>
  )
}

export function PlaygroundCard({
  sample,
  onClick,
  size = 'small',
}: {
  sample: string
  onClick: React.MouseEventHandler<HTMLDivElement> | undefined
  size?: 'small' | 'large'
}) {
  const sizes = {
    small: {
      p: '12px',
      fontSize: 0,
    },
    large: {
      p: 3,
      fontSize: 1,
    },
  }

  return (
    <Box
      onClick={onClick}
      sx={{
        ...sizes[size],
        borderRadius: 2,
        height: '100%',
        flex: 1,
        borderColor: 'border.muted',
        borderWidth: 1,
        borderStyle: 'solid',
        bg: 'var(--card-bgColor)',

        display: 'flex',
        boxShadow: 'var(--shadow-resting-small,var(--color-shadow-small))',
        transition: 'background-color .3s',
        gap: 3,
        ':hover': {
          bg: 'var(--control-transparent-bgColor-hover,var(--color-canvas-subtle))',
          cursor: 'pointer',
        },
      }}
    >
      <Box sx={{flex: 1}}>{sample}</Box>
      <Box sx={{color: 'fg.muted'}}>
        <ArrowUpRightIcon />
      </Box>
    </Box>
  )
}

function SidebarInfo({model}: {model: Model}) {
  const {task, properties, summary, tags} = model
  const [languagesIsOpen, setLanguagesIsOpen] = useState(false)

  const languages = properties.limits.languages.filter(language => language !== null)
  const shouldTruncateLanguages = languages.length > 12
  let languagesString: string | undefined

  if (languages.length === 1) {
    languagesString = languages[0]
  } else if (shouldTruncateLanguages && !languagesIsOpen) {
    languagesString = languages.slice(0, 12).join(', ')
  } else {
    const languagesButLast = languages.slice(0, languages.length - 1).join(', ')
    languagesString = `${languagesButLast}, and ${languages[languages.length - 1]}`
  }

  const modelClass = properties.limits.rate?.model_class

  const Heading = ({title, count}: {title: string; count?: number}) => (
    <Box sx={{display: 'flex', alignItems: 'baseline', gap: 1}}>
      <Text as="h3" sx={{fontWeight: 'bold', fontSize: [1, 1, 2]}}>
        {title}
      </Text>
      {count && <CounterLabel>{count}</CounterLabel>}
    </Box>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        paddingTop: '12px',
      }}
    >
      <Box
        sx={{
          display: ['none', 'none', 'flex'],
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Heading title="About" />
        <Text sx={{lineHeight: 1.5}}>{summary}</Text>
        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
          <a href={`/topics/${task}`} className="topic-tag topic-tag-link">
            {task}
          </a>
          {tags.map(tag => (
            <a href={`/topics/${tag}`} key={tag} className="topic-tag topic-tag-link">
              {tag}
            </a>
          ))}
        </Box>
      </Box>

      {properties.limits.languages.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Heading title="Languages" count={properties.limits.languages.length} />
          <Box>
            {languagesString}{' '}
            {shouldTruncateLanguages && !languagesIsOpen && (
              <>
                {' '}
                <Box
                  as="button"
                  aria-label="Reveal all languages"
                  sx={{color: 'fg.muted', display: 'inline', cursor: 'pointer', border: 0, bg: 'transparent'}}
                  onClick={() => setLanguagesIsOpen(true)}
                >
                  <EllipsisIcon />
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {modelClass && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Heading title="Model class" />
          <Box>
            <Label>{modelClass}</Label>
          </Box>
        </Box>
      )}

      {properties.limits.inputs.tokens > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Heading title="Context size" />
          <Box>{properties.limits.inputs.tokens.toLocaleString()} tokens</Box>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Heading title="Training" />
        <Box>{properties.training_data || 'Undisclosed'}</Box>
      </Box>
    </Box>
  )
}

try{ ModelLayout.displayName ||= 'ModelLayout' } catch {}
try{ BreadcrumbHeader.displayName ||= 'BreadcrumbHeader' } catch {}
try{ OverviewHeader.displayName ||= 'OverviewHeader' } catch {}
try{ PlaygroundHeader.displayName ||= 'PlaygroundHeader' } catch {}
try{ GiveFeedback.displayName ||= 'GiveFeedback' } catch {}
try{ MiniPlayground.displayName ||= 'MiniPlayground' } catch {}
try{ PlaygroundCard.displayName ||= 'PlaygroundCard' } catch {}
try{ SidebarInfo.displayName ||= 'SidebarInfo' } catch {}
try{ Heading.displayName ||= 'Heading' } catch {}